import {Absence, AbsenceType} from './types';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import HttpClient from '../../api/api';

export interface AbsencesState {
    items: Absence[];
}

const initialState: AbsencesState = {
    items: []
}

const upsertAbsence = (state: AbsencesState, absence: Absence) => {
    const index = state.items.findIndex((item) => item.id === absence.id);

    if (index !== -1) {
        state.items[index] = absence;
        return;
    }

    state.items.push(absence);
}

export const getAbsence = createAsyncThunk(
    'absences/get',
    async (absencesId: number) => {
        const response = await HttpClient.absencesApi.get<Absence>(absencesId);
        return response.data;
    }
)

export const getAbsences = createAsyncThunk(
    'absences/get-many',
    async (
        args: { dateFrom: string, dateTo: string, employees: Array<number>, types: Array<number> },
        { signal }
    ) => {
        const response = await HttpClient.absencesApi.getMany<Array<Absence>>(args.dateFrom, args.dateTo, args.employees, args.types, signal);
        return response.data;
    }
)

export const createAbsence = createAsyncThunk(
    'absences/create',
    async (absence: Absence) => {
        const response = await HttpClient.absencesApi.create<Absence>(absence);
        return response.data;
    }
)

export const updateAbsence = createAsyncThunk(
    'absences/update',
    async (absence: Absence) => {
        const response = await HttpClient.absencesApi.update<Absence>(absence.id, absence);
        return response.data;

    }
)

export const updateAbsenceType = createAsyncThunk(
    'absences/update-type',
    async (arg: { absenceId: number, type: AbsenceType }) => {
        const response = await HttpClient.absencesApi.updateType<Absence>(arg.absenceId, arg.type);
        return response.data;
    }
)

export const deleteAbsence = createAsyncThunk(
    'absences/delete',
    async (absenceId: number) => {
        const response = await HttpClient.absencesApi.delete(absenceId);
        return response.data;
    }
)

export const absenceBatchApprove = createAsyncThunk(
    'absences/batch-approve',
    async (args: { ids: Array<number>, approve: boolean }, { rejectWithValue }) => {
        if (args.ids.length < 1) {
            return { ids: [], approve: false }
        }
        try {
            const response = await HttpClient.absencesApi.batchApprove(args.ids, args.approve);
            if (response.status !== 200) {
                return rejectWithValue(response.statusText)
            }
            return args;
        } catch (error) {
            return rejectWithValue('Unable to approve absences')
        }
    }
)

export const absenceApprove = createAsyncThunk(
    'absences/approve',
    async (args: { id: number, approve: boolean }, { rejectWithValue }) => {
        try {
            const response = await HttpClient.absencesApi.updateApproved(args.id, args.approve);
            if (response.status !== 200) {
                return rejectWithValue(response.statusText)
            }
            return args;
        } catch (error) {
            return rejectWithValue('Unable to approve absence')
        }
    }
)

const absenceSlice = createSlice({
    name: 'absences',
    initialState,
    reducers: {
        clearAbsences: (state) => {
            state.items = initialState.items
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getAbsences.fulfilled, (state, action) => {
            state.items = action.payload;
        });
        builder.addCase(updateAbsence.fulfilled, (state, action) => {
            upsertAbsence(state, action.payload);
        });
        builder.addCase(createAbsence.fulfilled, (state, action) => {
            upsertAbsence(state, action.payload);
        });
        builder.addCase(updateAbsenceType.fulfilled, (state, action) => {
            upsertAbsence(state, action.payload);
        });
        builder.addCase(deleteAbsence.fulfilled, (state, action) => {
            state.items = state.items.filter((item) => item.id !== action.meta.arg);
        });
        builder.addCase(absenceBatchApprove.fulfilled, (state, action) => {
            action.payload.ids.forEach((x) => {
                const index = state.items.findIndex((y) => y.id === x);
                if (index != -1) {
                    state.items[index].approved = action.payload.approve;
                }
            });
        });
    }
})

export const { clearAbsences } = absenceSlice.actions;

export default absenceSlice.reducer;
